/**
 * AdminDataCoverage — `/admin/data-coverage`
 * -----------------------------------------------------------------------------
 * One-glance heatmap of structured-data coverage per country. Rows are
 * countries (largest first), columns are the ~11 data fields WUA degree
 * pages consume. Cells are colour-banded (green ≥85%, amber 40-85%, red
 * <40%) and clickable — clicking opens a drill-down drawer listing the
 * top unhydrated universities in that country/field combo, with copy-to-
 * clipboard uni_ids for editorial sprints.
 *
 * Backed by:
 *   GET /api/admin/data-coverage
 *   GET /api/admin/data-coverage/drilldown?country=&field=&limit=
 * Both require the admin session cookie (email in ADMIN_EMAILS).
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const BAND_STYLES = {
  green: "bg-emerald-100 text-emerald-900 border-emerald-200 hover:bg-emerald-200",
  amber: "bg-amber-100 text-amber-900 border-amber-200 hover:bg-amber-200",
  red:   "bg-red-100 text-red-900 border-red-200 hover:bg-red-200",
};

export default function AdminDataCoverage() {
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [drill, setDrill] = useState(null); // { country, field } or null

  useEffect(() => {
    (async () => {
      try {
        const [rMatrix, rTrend] = await Promise.all([
          axios.get(`${API}/api/admin/data-coverage`, { withCredentials: true }),
          axios.get(`${API}/api/admin/data-coverage/trend?days=30&buckets=6`, { withCredentials: true }),
        ]);
        setData(rMatrix.data);
        setTrend(rTrend.data);
      } catch (e) {
        setErr(e.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-stone-600" data-testid="data-coverage-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading coverage matrix…
      </div>
    );
  }
  if (err) {
    return (
      <div className="p-8" data-testid="data-coverage-error">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          <div className="font-medium">Couldn't load data coverage</div>
          <div className="mt-1 text-sm">{err}</div>
          <div className="mt-3 text-xs text-red-700">
            This page requires admin access — sign in with an ADMIN_EMAILS account.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-8 py-8" data-testid="admin-data-coverage-page">
      <div className="mb-6">
        <div className="label-uppercase text-[#4C1D95]">Admin · Data operations</div>
        <h1 className="mt-1 font-editorial text-3xl text-[#002147]">Data coverage matrix</h1>
        <p className="mt-2 text-sm text-stone-600 max-w-3xl">
          One row per country, one column per structured data field. Cell
          colour = coverage band (green ≥ 85%, amber 40–85%, red &lt; 40%).
          Click any cell to drill down into the top unhydrated universities
          for that combo — the queue an editorial sprint should attack.
        </p>
        <div className="mt-2 text-[11px] font-underground-sc tracking-wider text-stone-400">
          Generated: {new Date(data.generated_at).toLocaleString()}
          {trend && (
            <span className="ml-3">
              · Trend window: last {trend.days} days · {trend.buckets} buckets of {trend.window_days} days
            </span>
          )}
        </div>
      </div>

      <CoverageTable
        rows={data.rows}
        fields={data.fields}
        trend={trend}
        onDrill={(country, field) => setDrill({ country, field })}
      />

      {drill && (
        <DrillDown
          country={drill.country}
          field={drill.field}
          onClose={() => setDrill(null)}
        />
      )}
    </div>
  );
}

function CoverageTable({ rows, fields, trend, onDrill }) {
  const scopeGroups = useMemo(() => {
    const deg = fields.filter((f) => f.scope === "degree");
    const uni = fields.filter((f) => f.scope === "uni");
    return { deg, uni };
  }, [fields]);
  const seriesFor = (country, fieldKey) =>
    trend?.series?.[country]?.[fieldKey] || null;
  // Cache per-country sprint queues so clicking twice doesn't refetch.
  const [sprintByCountry, setSprintByCountry] = useState({});
  const [sprintLoading, setSprintLoading] = useState(null);
  const loadSprint = async (country) => {
    if (sprintByCountry[country] || sprintLoading === country) return;
    setSprintLoading(country);
    try {
      const r = await axios.get(
        `${API}/api/admin/data-coverage/sprint-queue`,
        { params: { country, limit: 3 }, withCredentials: true },
      );
      setSprintByCountry((prev) => ({ ...prev, [country]: r.data.queue }));
    } finally {
      setSprintLoading(null);
    }
  };

  return (
    <div className="overflow-x-auto border border-stone-200 rounded-xl bg-white">
      <table className="w-full text-[12px] border-collapse" data-testid="coverage-table">
        <thead className="bg-stone-50 sticky top-0 z-10">
          <tr>
            <th className="text-left py-2 px-3 font-medium text-stone-500 border-b border-stone-200 sticky left-0 bg-stone-50 z-20 min-w-[140px]">
              Country
            </th>
            <th className="text-right py-2 px-2 font-medium text-stone-500 border-b border-stone-200 min-w-[60px]">Unis</th>
            <th className="text-right py-2 px-2 font-medium text-stone-500 border-b border-stone-200 min-w-[60px]">Edit</th>
            <th className="text-right py-2 px-2 font-medium text-stone-500 border-b border-stone-200 min-w-[70px]">Degrees</th>
            {scopeGroups.deg.map((f) => (
              <th key={f.key} className="text-center py-2 px-2 font-medium text-stone-500 border-b border-stone-200 min-w-[90px]" title={f.description}>
                {f.label}
              </th>
            ))}
            <th className="border-b border-stone-200 border-l border-l-stone-300 px-2"></th>
            {scopeGroups.uni.map((f) => (
              <th key={f.key} className="text-center py-2 px-2 font-medium text-stone-500 border-b border-stone-200 min-w-[90px]" title={f.description}>
                {f.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.country} className="border-b border-stone-100 hover:bg-stone-50/40" data-testid={`coverage-row-${row.country}`}>
              <td className="py-2 px-3 font-medium text-[#002147] sticky left-0 bg-white z-10">{row.country}</td>
              <td className="text-right py-2 px-2 tabular-nums text-stone-600">{row.universities}</td>
              <td className="text-right py-2 px-2 tabular-nums text-stone-600">{row.editorial}</td>
              <td className="text-right py-2 px-2 tabular-nums text-stone-600">{row.degrees.toLocaleString()}</td>
              {scopeGroups.deg.map((f) => (
                <Cell
                  key={f.key}
                  row={row}
                  field={f}
                  series={seriesFor(row.country, f.key)}
                  onDrill={onDrill}
                />
              ))}
              <td className="border-l border-l-stone-300"></td>
              {scopeGroups.uni.map((f) => (
                <Cell
                  key={f.key}
                  row={row}
                  field={f}
                  series={seriesFor(row.country, f.key)}
                  onDrill={onDrill}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Cell({ row, field, series, onDrill }) {
  const f = row.fields[field.key];
  if (!f) return <td className="text-center text-stone-300">—</td>;
  const cls = BAND_STYLES[f.band] || "";
  const clickable = f.pct < 100 && (f.denominator > 0);
  const total = series ? series.reduce((a, b) => a + b, 0) : 0;
  return (
    <td className="px-1 py-1 text-center align-top">
      <button
        type="button"
        disabled={!clickable}
        onClick={() => clickable && onDrill(row.country, field)}
        data-testid={`cell-${row.country}-${field.key}`}
        data-band={f.band}
        className={`w-full rounded-md border px-2 py-1 tabular-nums text-[11px] transition-colors ${cls} ${
          clickable ? "cursor-pointer" : "cursor-default opacity-70"
        }`}
        title={`${f.populated.toLocaleString()} of ${f.denominator.toLocaleString()} — click to drill down${
          total > 0 ? `\n+${total.toLocaleString()} stamped in last 30 days` : ""
        }`}
      >
        <div>{f.pct.toFixed(1)}%</div>
        {series && total > 0 && (
          <Sparkline series={series} band={f.band} />
        )}
      </button>
    </td>
  );
}

function SprintQueue({ country, queue, loading, onLoad }) {
  // 24 countries × one tiny call → eager-load on mount. Renders a
  // 3-item ranked "sprint next" list below the country name so editors
  // see the highest-ROI universities to hydrate without leaving the row.
  useEffect(() => {
    if (queue == null && !loading) onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (queue == null) {
    return (
      <div className="mt-1 text-[9px] font-underground-sc tracking-wider text-stone-300" data-testid={`sprint-loading-${country}`}>
        {loading ? "loading…" : "· · ·"}
      </div>
    );
  }
  if (queue.length === 0) {
    return (
      <div className="mt-1 text-[9px] font-underground-sc tracking-wider text-emerald-600" data-testid={`sprint-empty-${country}`}>
        no red gaps
      </div>
    );
  }
  return (
    <div className="mt-1 flex flex-col gap-0.5" data-testid={`sprint-queue-${country}`}>
      <div className="text-[8px] font-underground-sc tracking-wider text-stone-400">
        Sprint next
      </div>
      {queue.map((u, i) => (
        <a
          key={u.uni_id}
          href={`/uni/${u.uni_id}`}
          target="_blank"
          rel="noreferrer"
          data-testid={`sprint-uni-${u.uni_id}`}
          className="inline-flex items-center gap-1 text-[10px] font-normal text-[#4C1D95] hover:underline max-w-[130px] truncate"
          title={`${u.name} · ${u.total_missing.toLocaleString()} missing / ${u.degree_total} total · score ${Math.round(u.score)}`}
        >
          <span className="text-stone-400 tabular-nums">{i + 1}.</span>
          <span className="truncate">{u.uni_id.replace(/^[a-z]{2}-/, "")}</span>
        </a>
      ))}
    </div>
  );
}


function Sparkline({ series, band }) {
  // Ultra-compact 60x14 SVG sparkline drawn inside the cell button. Uses
  // the band colour at reduced opacity so the eye reads it as "on top of"
  // the existing colour swatch, not as a separate visual element.
  const w = 60, h = 14, pad = 1;
  const max = Math.max(1, ...series);
  const stroke = band === "green" ? "#047857" : band === "amber" ? "#B45309" : "#B91C1C";
  const points = series.map((v, i) => {
    const x = pad + (i * (w - 2 * pad)) / (series.length - 1 || 1);
    const y = h - pad - (v / max) * (h - 2 * pad);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const total = series.reduce((a, b) => a + b, 0);
  return (
    <div
      className="mt-1 flex items-center gap-1 justify-center"
      data-testid="sparkline"
    >
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="opacity-90">
        {/* Baseline for context */}
        <line x1={pad} y1={h - pad} x2={w - pad} y2={h - pad} stroke={stroke} strokeOpacity={0.15} strokeWidth={0.5} />
        <polyline
          points={points}
          fill="none"
          stroke={stroke}
          strokeWidth={1.2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Highlight the most recent bucket */}
        {(() => {
          const [lastPx, lastPy] = points.split(" ").pop().split(",").map(parseFloat);
          return <circle cx={lastPx} cy={lastPy} r={1.6} fill={stroke} />;
        })()}
      </svg>
      <span className="text-[8px] font-mono opacity-70 leading-none">+{total.toLocaleString()}</span>
    </div>
  );
}

function DrillDown({ country, field, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(
          `${API}/api/admin/data-coverage/drilldown`,
          { params: { country, field: field.key, limit: 50 }, withCredentials: true },
        );
        setData(r.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [country, field.key]);

  const copyIds = () => {
    if (!data?.unis) return;
    const text = data.unis.map((u) => u.uni_id).join("\n");
    navigator.clipboard.writeText(text);
    toast.success(`Copied ${data.unis.length} uni_ids to clipboard`);
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-start justify-end"
      onClick={onClose}
      data-testid="drilldown-overlay"
    >
      <div
        className="w-full max-w-2xl h-full bg-white shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="drilldown-panel"
      >
        <header className="sticky top-0 bg-white border-b border-stone-200 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="label-uppercase text-[#4C1D95]">Drill-down · unhydrated</div>
            <h2 className="mt-1 font-editorial text-xl text-[#002147]">
              {country} · {field.label}
            </h2>
            {data?.total_unhydrated != null && (
              <div className="text-[12px] text-stone-500 mt-0.5">
                {data.total_unhydrated.toLocaleString()} unhydrated
                {field.scope === "degree" ? " uni(s) with missing degrees" : " uni(s)"}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="drilldown-close"
            className="p-1.5 rounded-full hover:bg-stone-100 text-stone-400 hover:text-stone-700"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="p-6">
          {loading && (
            <div className="flex items-center gap-2 text-stone-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          )}
          {!loading && data?.unis?.length === 0 && (
            <div className="text-stone-500 text-sm">Everything is hydrated here.</div>
          )}
          {!loading && data?.unis?.length > 0 && (
            <>
              <button
                type="button"
                onClick={copyIds}
                data-testid="drilldown-copy-ids"
                className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-[#4C1D95]/40 bg-[#F5F0FF] px-3 py-1 text-[11px] font-underground-sc tracking-wider text-[#4C1D95] hover:bg-[#4C1D95]/[0.08]"
              >
                Copy all uni_ids
              </button>
              <table className="w-full text-[13px]" data-testid="drilldown-table">
                <thead>
                  <tr className="border-b border-stone-200 text-left text-[11px] font-underground-sc tracking-wider text-stone-500">
                    <th className="py-2 pr-3">University</th>
                    <th className="py-2 px-2 text-right">Missing</th>
                    <th className="py-2 pl-2">Tier</th>
                  </tr>
                </thead>
                <tbody>
                  {data.unis.map((u) => (
                    <tr key={u.uni_id} className="border-b border-stone-100" data-testid={`drilldown-row-${u.uni_id}`}>
                      <td className="py-2 pr-3">
                        <div className="text-[#002147] font-medium">{u.name}</div>
                        <div className="text-[11px] font-mono text-stone-500">{u.uni_id}</div>
                      </td>
                      <td className="py-2 px-2 text-right tabular-nums">
                        {u.degree_total != null ? (
                          <>
                            {u.missing_count.toLocaleString()}
                            <span className="text-stone-400"> /{u.degree_total.toLocaleString()}</span>
                          </>
                        ) : (
                          "missing"
                        )}
                      </td>
                      <td className="py-2 pl-2">
                        <span className={
                          "inline-block rounded-full px-2 py-0.5 text-[10px] font-underground-sc tracking-wider " +
                          (u.source_tier === "editorial"
                            ? "bg-[#EDE9FE] text-[#4C1D95]"
                            : "bg-stone-100 text-stone-600")
                        }>
                          {u.source_tier}
                        </span>
                        <a
                          href={`/uni/${u.uni_id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="ml-2 inline-flex items-center gap-0.5 text-[11px] text-stone-400 hover:text-[#4C1D95]"
                        >
                          view <ExternalLink className="h-3 w-3" />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
